import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

/** @returns {Promise<Record<string, string>>} */
async function refreshCartSections() {
  /** @type {Record<string, string>} */
  const sections = {};

  await Promise.all(
    [...document.querySelectorAll('cart-items-component')].map(async (el) => {
      if (!(el instanceof HTMLElement) || !el.dataset.sectionId) return;
      sections[el.dataset.sectionId] = await sectionRenderer.renderSection(el.dataset.sectionId, {
        cache: false,
      });
    })
  );

  return sections;
}

/** @param {Record<string, unknown>} cart */
function dispatchCartAdd(cart, sourceId, data = {}) {
  document.dispatchEvent(
    new CartAddEvent(cart, sourceId, {
      source: 'mcp-cart-upsell',
      ...data,
    })
  );
}

/** @param {HTMLFormElement} form */
async function postCartAdd(form) {
  const idInput = form.querySelector('input[name="id"]');
  const qtyInput = form.querySelector('input[name="quantity"]');
  const sections = getCartSectionIds();

  /** @type {{ id: number, quantity: number, sections?: string, sections_url?: string }} */
  const payload = {
    id: Number(idInput instanceof HTMLInputElement ? idInput.value : 0),
    quantity: Math.max(1, parseInt(qtyInput instanceof HTMLInputElement ? qtyInput.value : '1', 10) || 1),
  };

  if (sections.length) {
    payload.sections = sections.join(',');
    payload.sections_url = window.location.pathname;
  }

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  return response.json();
}

/** @param {number} variantId @param {number} quantity */
async function postCartAddItem(variantId, quantity) {
  const sections = getCartSectionIds();
  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: variantId,
      quantity,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    }),
  });
  return response.json();
}

async function fetchCart() {
  const response = await fetch('/cart.js');
  return response.json();
}

function goToCart() {
  window.location.assign(Theme.routes.cart_url || '/cart');
}

class McpCartUpsell {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {HTMLElement | null} */
  #productsRoot = null;

  /** @type {HTMLElement | null} */
  #errorEl = null;

  /** @type {boolean} */
  #scrollLocked = false;

  /** @type {number} */
  #scrollY = 0;

  constructor() {
    const root = document.getElementById('mcp-cart-upsell');
    if (!root) return;

    this.#dialog = root.querySelector('dialog');
    this.#productsRoot = root.querySelector('[data-mcp-upsell-products]');
    this.#errorEl = root.querySelector('[data-mcp-upsell-error]');

    root.querySelector('[data-mcp-upsell-close]')?.addEventListener('click', () => this.close('stay'));
    root.querySelector('[data-mcp-upsell-skip]')?.addEventListener('click', () => this.close('cart'));
    root.querySelector('[data-mcp-upsell-continue]')?.addEventListener('click', () => this.close('stay'));

    this.#dialog?.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.close('stay');
    });

    this.#dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close('stay');
    });

    this.#bindProductPage();
  }

  #bindProductPage() {
    const configEl = document.querySelector('[data-mcp-upsell-config]');
    const form = document.getElementById('mcp-product-form');
    if (!(configEl instanceof HTMLScriptElement) || !(form instanceof HTMLFormElement)) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent || '{}');
    } catch {
      return;
    }

    if (!Array.isArray(config.products) || config.products.length === 0) return;

    document.getElementById('mcp-cart-upsell')?.removeAttribute('hidden');

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#handleMainAdd(form, config);
    });
  }

  /** @param {HTMLFormElement} form */
  async #handleMainAdd(form, config) {
    const submitButton = form.querySelector('[data-add-button]');
    if (submitButton instanceof HTMLButtonElement) submitButton.disabled = true;

    this.#hideError();

    const formData = new FormData(form);

    try {
      const response = await postCartAdd(form);
      if (response.status) {
        this.#showError(response.description || response.message || 'Impossible d’ajouter au panier.');
        return;
      }

      const cart = await fetchCart();
      const sections = await refreshCartSections();

      dispatchCartAdd(cart, form.id, {
        itemCount: cart.item_count,
        productId: config.productId,
        variantId: String(formData.get('id') || ''),
        sections,
        skipDrawerOpen: true,
      });

      this.#renderProducts(config);
      this.#open(config);
    } catch (error) {
      console.error(error);
      this.#showError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      if (submitButton instanceof HTMLButtonElement) submitButton.disabled = false;
    }
  }

  /** @param {Record<string, unknown>} config */
  #renderProducts(config) {
    if (!this.#productsRoot) return;

    const template = document.getElementById('mcp-cart-upsell-product-template');
    if (!(template instanceof HTMLTemplateElement)) return;

    this.#productsRoot.replaceChildren();

    const titleEl = document.querySelector('[data-mcp-upsell-title]');
    const subEl = document.querySelector('[data-mcp-upsell-sub]');
    const themeTitle = typeof config.themeTitle === 'string' ? config.themeTitle : '';

    if (titleEl) {
      titleEl.textContent = themeTitle ? `Extras ${themeTitle}` : 'Ajoutez des extras';
    }
    if (subEl) {
      subEl.textContent = 'Ajoutez les produits que vous souhaitez à votre commande.';
    }

    config.products.forEach((product) => {
      const node = template.content.firstElementChild?.cloneNode(true);
      if (!(node instanceof HTMLElement)) return;

      node.dataset.variantId = String(product.variantId);
      node.dataset.available = product.available ? 'true' : 'false';

      const image = node.querySelector('[data-mcp-upsell-image]');
      const title = node.querySelector('[data-mcp-upsell-product-title]');
      const price = node.querySelector('[data-mcp-upsell-product-price]');
      const qtyInput = node.querySelector('[data-mcp-upsell-qty]');
      const decrease = node.querySelector('[data-mcp-upsell-decrease]');
      const increase = node.querySelector('[data-mcp-upsell-increase]');
      const addButton = node.querySelector('[data-mcp-upsell-add-item]');
      const addLabel = node.querySelector('[data-mcp-upsell-add-label]');

      if (image instanceof HTMLImageElement) {
        image.src = product.image || '';
        image.alt = product.imageAlt || product.title || '';
      }
      if (title) title.textContent = product.title || '';
      if (price) price.textContent = product.priceFormatted || '';

      if (!(qtyInput instanceof HTMLInputElement) || !(addButton instanceof HTMLButtonElement)) return;

      if (!product.available) {
        qtyInput.disabled = true;
        decrease?.setAttribute('disabled', 'true');
        increase?.setAttribute('disabled', 'true');
        addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Indisponible';
        if (price) price.textContent = 'Indisponible';
        this.#productsRoot.appendChild(node);
        return;
      }

      const setQty = (next) => {
        qtyInput.value = String(Math.max(1, Math.min(99, next)));
      };

      decrease?.addEventListener('click', () => setQty((parseInt(qtyInput.value, 10) || 1) - 1));
      increase?.addEventListener('click', () => setQty((parseInt(qtyInput.value, 10) || 1) + 1));
      qtyInput.addEventListener('change', () => {
        const parsed = parseInt(qtyInput.value, 10);
        setQty(Number.isFinite(parsed) ? parsed : 1);
      });

      addButton.addEventListener('click', () => {
        this.#addProduct(Number(product.variantId), qtyInput, addButton, addLabel);
      });

      this.#productsRoot.appendChild(node);
    });
  }

  /**
   * @param {number} variantId
   * @param {HTMLInputElement} qtyInput
   * @param {HTMLButtonElement} addButton
   * @param {Element | null} addLabel
   */
  async #addProduct(variantId, qtyInput, addButton, addLabel) {
    const quantity = Math.max(1, parseInt(qtyInput.value, 10) || 1);

    addButton.disabled = true;
    this.#hideError();

    try {
      const response = await postCartAddItem(variantId, quantity);
      if (response.status) {
        this.#showError(response.description || response.message || 'Impossible d’ajouter au panier.');
        addButton.disabled = false;
        return;
      }

      const cart = await fetchCart();
      const sections = await refreshCartSections();

      dispatchCartAdd(cart, 'mcp-cart-upsell-extras', {
        itemCount: cart.item_count,
        sections,
        skipDrawerOpen: true,
      });

      if (addLabel instanceof HTMLElement) {
        addLabel.textContent = 'Ajouté ✓';
      }
      addButton.classList.add('is-added');
      addButton.disabled = false;

      window.setTimeout(() => {
        if (addLabel instanceof HTMLElement) {
          addLabel.textContent = 'Ajouter au panier';
        }
        addButton.classList.remove('is-added');
      }, 2500);
    } catch (error) {
      console.error(error);
      this.#showError('Une erreur est survenue. Veuillez réessayer.');
      addButton.disabled = false;
    }
  }

  /** @param {Record<string, unknown>} config */
  #open(config) {
    if (!this.#dialog) return;

    const successText = document.querySelector('[data-mcp-upsell-success-text]');
    if (successText instanceof HTMLElement && typeof config.successMessage === 'string') {
      successText.textContent = config.successMessage;
    }

    this.#scrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#scrollY}px`;
    this.#scrollLocked = true;

    this.#dialog.showModal();
  }

  /** @param {'cart' | 'stay'} [destination] */
  close(destination = 'stay') {
    if (!this.#dialog?.open) return;

    this.#dialog.close();

    if (this.#scrollLocked) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#scrollY, behavior: 'instant' });
      this.#scrollLocked = false;
    }

    if (destination === 'cart') {
      goToCart();
    }
  }

  #showError(message) {
    if (!this.#errorEl) return;
    this.#errorEl.textContent = message;
    this.#errorEl.classList.remove('hidden');
  }

  #hideError() {
    this.#errorEl?.classList.add('hidden');
  }
}

new McpCartUpsell();
